// File: frontend/src/components/Opportunities/OpportunityForm.jsx
// Opportunity form component for creating and editing opportunities

import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from 'react-query';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  UserIcon,
  CalendarIcon,
  ChartBarIcon,
  TagIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const OpportunityForm = ({ 
  isOpen, 
  onClose, 
  opportunity = null, 
  contactId = null, 
  companyId = null, 
  onSuccess 
}) => {
  const isEditing = !!opportunity;
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    value: '',
    source: '',
    stage: 'lead',
    probability: '10',
    expected_close_date: '',
    contact_id: contactId || '',
    company_id: companyId || '',
    user_id: '',
    lost_reason: ''
  });

  const [errors, setErrors] = useState({});

  // Load existing opportunity data when editing
  useEffect(() => {
    if (opportunity) {
      setFormData({
        title: opportunity.title || '',
        description: opportunity.description || '',
        value: opportunity.value || '',
        source: opportunity.source || '',
        stage: opportunity.stage || 'lead',
        probability: opportunity.probability || '10',
        expected_close_date: opportunity.expected_close_date ? 
          opportunity.expected_close_date.split('T')[0] : '',
        contact_id: opportunity.contact_id || '',
        company_id: opportunity.company_id || '',
        user_id: opportunity.user_id || '',
        lost_reason: opportunity.lost_reason || ''
      });
    }
  }, [opportunity]);

  // Reset form when contactId or companyId props change
  useEffect(() => {
    if (!isEditing) {
      setFormData(prev => ({
        ...prev,
        contact_id: contactId || '',
        company_id: companyId || ''
      }));
    }
  }, [contactId, companyId, isEditing]);

  // Fetch contacts for dropdown
  const { data: contacts = [] } = useQuery(
    'contacts-for-form',
    async () => {
      const response = await api.get('/contacts?limit=100');
      return response.data.contacts || [];
    },
    { enabled: isOpen }
  );

  // Fetch companies for dropdown
  const { data: companies = [] } = useQuery(
    'companies-for-form',
    async () => {
      const response = await api.get('/companies?limit=100');
      return response.data.companies || [];
    },
    { enabled: isOpen }
  );

  // Fetch users for dropdown
  const { data: users = [] } = useQuery(
    'users-for-form',
    async () => {
      const response = await api.get('/users?limit=100');
      return response.data.data || response.data.users || [];
    },
    { enabled: isOpen }
  );

  // Auto-complete company when contact is selected
  useEffect(() => {
    if (formData.contact_id && contacts.length > 0) {
      const selectedContact = contacts.find(c => c.id.toString() === formData.contact_id.toString());
      if (selectedContact && selectedContact.company_id && !formData.company_id) {
        setFormData(prev => ({
          ...prev,
          company_id: selectedContact.company_id.toString()
        }));
      }
    }
  }, [formData.contact_id, contacts, formData.company_id]);

  // Create/Update mutation
  const mutation = useMutation(
    async (data) => {
      if (isEditing) {
        return await api.put(`/opportunities/${opportunity.id}`, data);
      } else {
        return await api.post('/opportunities', data);
      }
    },
    {
      onSuccess: () => {
        toast.success(isEditing ? 'Oportunidade atualizada com sucesso!' : 'Oportunidade criada com sucesso!');
        onSuccess();
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.message || 'Erro ao salvar oportunidade';
        toast.error(errorMsg);
        if (error.response?.data?.errors) {
          setErrors(error.response.data.errors);
        }
      }
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Auto-adjust probability based on stage
    if (name === 'stage') {
      const stageConfig = stages.find(s => s.value === value);
      if (stageConfig && !isEditing) {
        setFormData(prev => ({
          ...prev,
          probability: stageConfig.defaultProbability.toString()
        }));
      }
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    // Basic validation
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Título é obrigatório';
    }
    
    if (!formData.contact_id && !formData.company_id) {
      newErrors.contact_id = 'Pelo menos um contato ou empresa deve ser selecionado';
      newErrors.company_id = 'Pelo menos um contato ou empresa deve ser selecionado';
    }
    
    if (formData.value && parseFloat(formData.value) <= 0) {
      newErrors.value = 'Valor deve ser positivo';
    }
    
    if (formData.probability && (parseFloat(formData.probability) < 0 || parseFloat(formData.probability) > 100)) {
      newErrors.probability = 'Probabilidade deve estar entre 0 e 100';
    }
    
    if (formData.expected_close_date) {
      const selectedDate = new Date(formData.expected_close_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        newErrors.expected_close_date = 'Data esperada não pode ser no passado';
      }
    }
    
    if (formData.stage === 'closed_lost' && !formData.lost_reason.trim()) {
      newErrors.lost_reason = 'Motivo da perda é obrigatório para oportunidades perdidas';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Prepare data for API
    const submitData = {
      ...formData,
      value: formData.value ? parseFloat(formData.value) : null,
      probability: formData.probability ? parseInt(formData.probability) : null,
      contact_id: formData.contact_id || null,
      company_id: formData.company_id || null,
      user_id: formData.user_id || null,
      expected_close_date: formData.expected_close_date || null,
      lost_reason: formData.stage === 'closed_lost' ? formData.lost_reason : null
    };

    mutation.mutate(submitData);
  };

  const stages = [
    { value: 'lead', label: 'Lead', defaultProbability: 10 },
    { value: 'qualified', label: 'Qualificado', defaultProbability: 25 },
    { value: 'proposal', label: 'Proposta', defaultProbability: 50 },
    { value: 'negotiation', label: 'Negociação', defaultProbability: 75 },
    { value: 'closed_won', label: 'Fechou Ganho', defaultProbability: 100 },
    { value: 'closed_lost', label: 'Fechou Perdido', defaultProbability: 0 }
  ];

  const sources = [
    'Website',
    'Google Ads',
    'Facebook Ads',
    'LinkedIn',
    'Cold Call',
    'Referência',
    'Email Marketing',
    'Evento',
    'Partner',
    'Outro'
  ];

  const formatCurrency = (value) => {
    if (!value || isNaN(value)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditing ? 'Editar Oportunidade' : 'Nova Oportunidade'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Informações Básicas</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Título *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.title ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Implementação de CRM para Empresa XYZ"
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição
                  </label>
                  <div className="relative">
                    <DocumentTextIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows="3"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Descreva os detalhes da oportunidade..."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor (R$)
                  </label>
                  <div className="relative">
                    <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="number"
                      name="value"
                      value={formData.value}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.value ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  {errors.value && (
                    <p className="mt-1 text-sm text-red-600">{errors.value}</p>
                  )}
                  {formData.value && (
                    <p className="mt-1 text-sm text-gray-500">
                      {formatCurrency(formData.value)}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fonte
                  </label>
                  <div className="relative">
                    <TagIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <select
                      name="source"
                      value={formData.source}
                      onChange={handleChange}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecionar fonte</option>
                      {sources.map(source => (
                        <option key={source} value={source}>
                          {source}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Pipeline Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Pipeline</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stage *
                  </label>
                  <select
                    name="stage"
                    value={formData.stage}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {stages.map(stage => (
                      <option key={stage.value} value={stage.value}>
                        {stage.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Probabilidade (%)
                  </label>
                  <div className="relative">
                    <ChartBarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="number"
                      name="probability"
                      value={formData.probability}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.probability ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="0"
                      min="0"
                      max="100"
                    />
                  </div>
                  {errors.probability && (
                    <p className="mt-1 text-sm text-red-600">{errors.probability}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Esperada de Fechamento
                  </label>
                  <div className="relative">
                    <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="date"
                      name="expected_close_date"
                      value={formData.expected_close_date}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.expected_close_date ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {errors.expected_close_date && (
                    <p className="mt-1 text-sm text-red-600">{errors.expected_close_date}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Relationships */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Relacionamentos</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contato
                  </label>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <select
                      name="contact_id"
                      value={formData.contact_id}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.contact_id ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Selecionar contato</option>
                      {contacts.map(contact => (
                        <option key={contact.id} value={contact.id}>
                          {contact.first_name} {contact.last_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.contact_id && (
                    <p className="mt-1 text-sm text-red-600">{errors.contact_id}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Empresa
                  </label>
                  <div className="relative">
                    <BuildingOfficeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <select
                      name="company_id"
                      value={formData.company_id}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.company_id ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Selecionar empresa</option>
                      {companies.map(company => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.company_id && (
                    <p className="mt-1 text-sm text-red-600">{errors.company_id}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Responsável
                  </label>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <select
                      name="user_id"
                      value={formData.user_id}
                      onChange={handleChange}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecionar responsável</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.first_name} {user.last_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Lost Reason - Only show if stage is closed_lost */}
            {formData.stage === 'closed_lost' && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
                  Motivo da Perda
                </h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descreva o motivo da perda *
                  </label>
                  <textarea
                    name="lost_reason"
                    value={formData.lost_reason}
                    onChange={handleChange}
                    rows="3"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.lost_reason ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Preço muito alto, não teve orçamento aprovado, escolheu concorrente..."
                  />
                  {errors.lost_reason && (
                    <p className="mt-1 text-sm text-red-600">{errors.lost_reason}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-4 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={mutation.isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {mutation.isLoading 
                ? (isEditing ? 'Atualizando...' : 'Criando...') 
                : (isEditing ? 'Atualizar Oportunidade' : 'Criar Oportunidade')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OpportunityForm;